import { updateDataSourceSchedule } from "../../../../api/dataSourceScheduleApi";
import {
  runCultureSource,
  updateCultureSource,
  listCultureSourceRuns,
} from "../../../../api/cultureSourceControls";
import { runFinanceFxSource } from "../../../../api/financeFxSourceControls";
import {
  enableFoodBranchLocatorSource,
  disableFoodBranchLocatorSource,
  enableFoodInputReferenceSource,
  disableFoodInputReferenceSource,
  enableFoodOsmPoiSource,
  disableFoodOsmPoiSource,
  enableFoodRetailSource,
  disableFoodRetailSource,
  enableFoodSipsaSource,
  disableFoodSipsaSource,
  runFoodBranchLocatorSourceIngestion,
  runFoodInputReferenceSourceIngestion,
  runFoodOsmPoiSourceIngestion,
  runFoodRetailIngestion,
  runFoodSipsaSourceIngestion,
} from "../../../../api/foodApi";
import { runGa4AnalyticsAdminSource, updateGa4AnalyticsAdminSource } from "../../../../api/ga4AnalyticsSourceControls";
import { fetchTransportJobRuns, runTransportJobNow } from "../../../../api/transportSchedulerApi";
import { runVendorDirectoryAdminSource } from "../../../../api/vendorDirectorySourceControls";
import {
  disableVehicleMarketSourceSchedule,
  enableVehicleMarketSourceSchedule,
  getVehicleMarketSourceRuns,
  runVehicleMarketSource,
  stopVehicleMarketSource,
  updateVehicleMarketSource,
} from "../../../../api/vehicleMarketSourceApi";
import { RETAIL_INGESTION_LIMITS } from "../food/foodRetailIngestionHelpers";
import {
  isFoodBranchLocatorDataHealthSource,
  isFoodInputReferenceDataHealthSource,
  isFoodOsmPoiDataHealthSource,
  isFoodRetailSupermarketDataHealthSource,
  isFoodSipsaDataHealthSource,
} from "../../../../constants/foodSourceConstants";
import {
  fetchRealEstateSourceRuns,
  runRealEstateSource,
  stopRealEstateSource,
  updateRealEstateSource,
} from "../../../../services/realEstateSourceControls";
import {
  fetchWeatherSourceHealth,
  runWeatherSource,
  updateWeatherSource,
} from "../../../../services/weatherSourceControls";
import { OVERLAY_SCHEDULE_DOMAINS } from "./dataSourceScheduleUtils";

function sourceCode(source) {
  return source?.sourceKey || source?.sourceCode || source?.id;
}

function vehicleId(source) {
  return source?.id || source?.sourceKey;
}

function foodEnablePair(source) {
  if (isFoodRetailSupermarketDataHealthSource(source)) {
    return { enable: enableFoodRetailSource, disable: disableFoodRetailSource };
  }
  if (isFoodSipsaDataHealthSource(source)) {
    return { enable: enableFoodSipsaSource, disable: disableFoodSipsaSource };
  }
  if (isFoodInputReferenceDataHealthSource(source)) {
    return { enable: enableFoodInputReferenceSource, disable: disableFoodInputReferenceSource };
  }
  if (isFoodOsmPoiDataHealthSource(source)) {
    return { enable: enableFoodOsmPoiSource, disable: disableFoodOsmPoiSource };
  }
  if (isFoodBranchLocatorDataHealthSource(source)) {
    return { enable: enableFoodBranchLocatorSource, disable: disableFoodBranchLocatorSource };
  }
  return null;
}

export function canToggleDataSource(source) {
  const domain = source?.domainId;
  if (["real_estate", "weather", "culture", "vehicle_market", "analytics"].includes(domain)) {
    return true;
  }
  if (domain === "food") {
    return Boolean(foodEnablePair(source));
  }
  return false;
}

export function canScheduleDataSource(source) {
  if (source?.supportsSchedule === false) return false;
  return true;
}

export function normalizeDataSourceRun(run = {}) {
  return {
    id: run.runId || run.id || run.checkedAt || run.startedAt || run.createdAt,
    status: run.status || run.lastStatus || run.healthStatus || "unknown",
    startedAt: run.startedAt || run.checkedAt || run.createdAt || run.finishedAt || null,
    finishedAt: run.finishedAt || run.completedAt || null,
    durationMs: run.durationMs ?? run.latencyMs ?? null,
    triggeredBy: run.triggeredBy || run.trigger || null,
    recordsFound: run.recordsFound ?? run.recordsReceived ?? run.recordCount ?? 0,
    recordsCreated: run.recordsCreated ?? run.recordsInserted ?? 0,
    recordsUpdated: run.recordsUpdated ?? 0,
    errorCount: run.errorCount ?? (run.errorMessage ? 1 : 0),
    errorMessage:
      run.errorMessage ||
      run.lastErrorMessage ||
      run.message ||
      (Array.isArray(run.errors) && run.errors[0]?.message) ||
      null,
    warningCount: run.warningCount ?? 0,
  };
}

export async function toggleDataSourceEnabled(source, nextEnabled) {
  const domain = source.domainId;
  const key = sourceCode(source);

  if (domain === "real_estate") {
    return updateRealEstateSource(key, { enabled: nextEnabled });
  }
  if (domain === "weather") {
    return updateWeatherSource(key, { isActive: nextEnabled });
  }
  if (domain === "culture") {
    return updateCultureSource(key, { enabled: nextEnabled });
  }
  if (domain === "vehicle_market") {
    return updateVehicleMarketSource(vehicleId(source), {
      isActive: nextEnabled,
      enabled: nextEnabled,
    });
  }
  if (domain === "analytics") {
    return updateGa4AnalyticsAdminSource(key, { enabled: nextEnabled });
  }
  if (domain === "food") {
    const pair = foodEnablePair(source);
    if (!pair) {
      throw new Error("This food source does not have an on/off control.");
    }
    if (nextEnabled) {
      return pair.enable(key, {
        confirm: true,
        enabledBy: "admin",
        notes: "Enabled from data source dashboard.",
      });
    }
    return pair.disable(key, {
      confirm: true,
      disabledBy: "admin",
      reason: "Disabled from data source dashboard.",
    });
  }

  throw new Error("This source does not have a collection on/off switch.");
}

export async function saveDataSourceSchedule(source, payload) {
  const domain = source.domainId;
  const key = sourceCode(source);

  if (domain === "real_estate") {
    return updateRealEstateSource(key, payload);
  }
  if (domain === "weather") {
    return updateWeatherSource(key, payload);
  }
  if (domain === "vehicle_market") {
    const id = vehicleId(source);
    if (payload.scheduleEnabled === false) {
      return disableVehicleMarketSourceSchedule(id);
    }
    if (payload.scheduleEnabled === true) {
      return enableVehicleMarketSourceSchedule(id, {
        intervalMinutes: payload.scheduleIntervalMinutes,
        scheduleIntervalMinutes: payload.scheduleIntervalMinutes,
      });
    }
    return updateVehicleMarketSource(id, payload);
  }
  if (domain === "analytics") {
    return updateGa4AnalyticsAdminSource(key, {
      enabled: payload.scheduleEnabled === true,
      pollIntervalMinutes: payload.scheduleIntervalMinutes || source.pollIntervalMinutes || 60,
    });
  }
  if (OVERLAY_SCHEDULE_DOMAINS.includes(domain) || domain === "vendor_directory") {
    return updateDataSourceSchedule(domain === "vendor_directory" ? "vendor_directory" : domain, key, payload);
  }

  throw new Error("Scheduling is not available for this source.");
}

export async function runDataSourceNow(source) {
  const domain = source.domainId;
  const key = sourceCode(source);

  if (domain === "real_estate") {
    return runRealEstateSource(key);
  }
  if (domain === "weather") {
    return runWeatherSource(key);
  }
  if (domain === "culture") {
    return runCultureSource(key, { apply: true });
  }
  if (domain === "vehicle_market") {
    return runVehicleMarketSource(vehicleId(source));
  }
  if (domain === "finance") {
    return runFinanceFxSource(key);
  }
  if (domain === "analytics") {
    return runGa4AnalyticsAdminSource(key);
  }
  if (domain === "vendor_directory") {
    return runVendorDirectoryAdminSource(source.sourceId || key);
  }
  if (domain === "transport") {
    if (!source.transportJobKey) {
      throw new Error("No automated transport scheduler job is mapped to this source.");
    }
    return runTransportJobNow(source.transportJobKey, { triggeredBy: "admin:data-source-detail" });
  }
  if (domain === "food") {
    const dryRun = !source.enabled;
    if (isFoodRetailSupermarketDataHealthSource(source)) {
      return runFoodRetailIngestion({
        sourceKey: key,
        dryRun,
        useMock: false,
        maxCategories: RETAIL_INGESTION_LIMITS.DEFAULT_MAX_CATEGORIES,
        maxProductsPerCategory: RETAIL_INGESTION_LIMITS.DEFAULT_MAX_PRODUCTS_PER_CATEGORY,
        categoryUrls: [],
      });
    }
    if (isFoodSipsaDataHealthSource(source)) {
      return runFoodSipsaSourceIngestion(key, { dryRun });
    }
    if (isFoodInputReferenceDataHealthSource(source)) {
      return runFoodInputReferenceSourceIngestion(key, { dryRun });
    }
    if (isFoodOsmPoiDataHealthSource(source)) {
      return runFoodOsmPoiSourceIngestion(key, { dryRun });
    }
    if (isFoodBranchLocatorDataHealthSource(source)) {
      return runFoodBranchLocatorSourceIngestion(key, { dryRun });
    }
    throw new Error("No automated runner is mapped to this food data source yet.");
  }

  throw new Error("Manual runs are not supported for this data source.");
}

export function canStopDataSource(source) {
  if (!source) return false;
  const latestStatus = String(
    source.latestRun?.status ||
      source.lastStatus ||
      source.lastRunStatus ||
      source.health ||
      ""
  ).toLowerCase();
  const isRunning =
    Boolean(source.isRunning) ||
    Boolean(source.activeRunId) ||
    source.health === "running" ||
    source.health === "queued" ||
    latestStatus === "running" ||
    latestStatus === "queued" ||
    latestStatus === "started";
  if (!isRunning) return false;
  if (source.domainId === "real_estate" || source.domainId === "vehicle_market") {
    return true;
  }
  return Boolean(source.supportsStop);
}

export async function stopDataSourceRun(source) {
  const domain = source.domainId;
  const key = sourceCode(source);

  if (domain === "real_estate") {
    return stopRealEstateSource(key);
  }
  if (domain === "vehicle_market") {
    return stopVehicleMarketSource(vehicleId(source));
  }

  throw new Error("Stopping an in-progress sync is not supported for this data source yet.");
}

export async function fetchDataSourceRuns(source, { limit = 25 } = {}) {
  const domain = source.domainId;
  const key = sourceCode(source);

  if (domain === "real_estate") {
    const data = await fetchRealEstateSourceRuns(key, { limit });
    return (data?.runs || []).map(normalizeDataSourceRun);
  }
  if (domain === "weather") {
    const data = await fetchWeatherSourceHealth(key, { limit });
    return (data?.records || []).map(normalizeDataSourceRun);
  }
  if (domain === "culture") {
    const data = await listCultureSourceRuns(key, { limit });
    return (data?.runs || data?.records || []).map(normalizeDataSourceRun);
  }
  if (domain === "vehicle_market") {
    const data = await getVehicleMarketSourceRuns(vehicleId(source), { limit });
    return (data?.records || data?.runs || []).map(normalizeDataSourceRun);
  }
  if (domain === "transport" && source.transportJobKey) {
    const data = await fetchTransportJobRuns(source.transportJobKey, { limit });
    return (data?.runs || data?.records || []).map(normalizeDataSourceRun);
  }

  const latest = source.latestRun;
  if (latest) {
    return [
      normalizeDataSourceRun({
        ...latest,
        errorMessage: source.lastErrorMessage || latest.errorMessage,
      }),
    ];
  }
  return [];
}
